/**
 * Caffeine, worked out from what was already logged.
 *
 * No chain prints milligrams and no label has to, so a caffeine figure can
 * either be a field nobody fills in or an estimate from the drink and its
 * volume. This is the second: every drink in the diary already carries a name
 * and a number of millilitres, which is exactly what a rough milligram total
 * needs, and nothing extra has to be typed to get one.
 *
 * The figures are per 100 ml of the drink as served, not of the coffee in it —
 * a latte is one shot in a lot of milk, which is why it is weaker per
 * millilitre than an americano and stronger in the cup than either number
 * suggests on its own.
 */

export interface CaffeineSource {
  /** Matched against the logged name, first hit wins, so order is meaning. */
  pattern: RegExp
  mgPer100ml: number
  label: string
}

export const CAFFEINE_SOURCES: readonly CaffeineSource[] = [
  { pattern: /espres|ristret/i, mgPer100ml: 210, label: 'Espresso' },
  { pattern: /(dvigub|double)\s*espres/i, mgPer100ml: 210, label: 'Dvigubas espresso' },
  {
    pattern: /americ|juoda kava|filtruot|filter coffee|black coffee/i,
    mgPer100ml: 40,
    label: 'Americano',
  },
  // Before the macchiato line below it, and not a spelling quibble: a latte
  // macchiato is one shot poured into a tall glass of milk, while a macchiato
  // proper is a shot with a spoonful of foam on it. Matching the longer name
  // first is what keeps a 400 ml milky drink from being rated at cortado
  // strength — nearly a day's caffeine from one glass.
  {
    pattern: /latt?[eė]?\s*(?:macchiat|makiat)/i,
    mgPer100ml: 25,
    label: 'Latte macchiato',
  },
  { pattern: /flat\s*white|kortado|cortado|macchiat|makiat/i, mgPer100ml: 45, label: 'Flat white' },
  {
    // "Latė" is how the counter in Vilnius spells it, so the double t is not
    // enough on its own.
    pattern: /latt|\blat[eė]|kapu[čc]|capp?u|moka|mocha|kavos gėrim/i,
    mgPer100ml: 25,
    label: 'Latte',
  },
  { pattern: /matcha/i, mgPer100ml: 28, label: 'Matcha' },
  { pattern: /žalioji arbata|zalioji arbata|green tea/i, mgPer100ml: 12, label: 'Žalioji arbata' },
  { pattern: /juodoji arbata|black tea|chai/i, mgPer100ml: 20, label: 'Juodoji arbata' },
  { pattern: /energ|red bull|monster|burn/i, mgPer100ml: 32, label: 'Energinis' },
  { pattern: /cola|kola|pepsi/i, mgPer100ml: 10, label: 'Kola' },
  // Last, so every more specific coffee above wins first. A bare "kava" with no
  // qualifier is most often brewed.
  { pattern: /\bkav[ao]\b|coffee/i, mgPer100ml: 40, label: 'Kava' },
]

/** Milligrams in one logged drink, or null when it is not a caffeine drink. */
export function caffeineMg(name: string, volumeMl: number): number | null {
  if (!Number.isFinite(volumeMl) || volumeMl <= 0) return null
  const source = CAFFEINE_SOURCES.find((candidate) => candidate.pattern.test(name))
  if (!source) return null
  return Math.round((source.mgPer100ml * volumeMl) / 100)
}

export interface CaffeineEntry {
  name: string
  /** Millilitres. A weighed food is not a drink and never counts here. */
  volumeMl: number | null
}

/**
 * A day's caffeine.
 *
 * Deliberately silent about anything measured in grams: chocolate has caffeine
 * in it, but a number that quietly folds in a biscuit is a number nobody can
 * check against how they feel.
 */
export function dayCaffeineMg(entries: readonly CaffeineEntry[]): number {
  return entries.reduce((total, entry) => {
    if (entry.volumeMl == null) return total
    return total + (caffeineMg(entry.name, entry.volumeMl) ?? 0)
  }, 0)
}

/**
 * EFSA's single-dose and daily thresholds for a healthy adult: 400 mg across a
 * day, and 200 mg at once. Shown as a limit rather than a target, the way salt
 * and saturated fat are.
 */
export const CAFFEINE_DAILY_LIMIT_MG = 400

export const CAFFEINE_SINGLE_DOSE_LIMIT_MG = 200

/**
 * Caffeine's elimination half-life in a healthy adult — the middle of the 3–7 h
 * range, and long enough that the afternoon coffee is a sleep decision rather
 * than an afternoon one.
 */
export const CAFFEINE_HALF_LIFE_HOURS = 5

/** The hour the residual is measured at, being roughly when people go to bed. */
export const CAFFEINE_BEDTIME_HOUR = 23

/**
 * How much may still be circulating at bedtime before it is worth mentioning.
 *
 * Not a health threshold — nothing bad happens at 51 mg. It is the point at
 * which the effect on sleep architecture is measurable: caffeine taken six
 * hours before bed cuts about an hour of sleep in controlled trials, and people
 * reliably fail to notice, which is exactly why an app should say it.
 */
export const CAFFEINE_BEDTIME_LIMIT_MG = 50

export interface CaffeineDose {
  mg: number
  /** Wall-clock hour it was drunk at, 0–23. */
  atHour: number
}

/**
 * What is left of the day's caffeine at a given hour.
 *
 * First-order elimination, which is what caffeine follows at ordinary doses: a
 * 150 mg latte at four in the afternoon is still about 50 mg at eleven. Doses
 * later than the hour asked about contribute nothing rather than counting
 * backwards, so this can be called with a bedtime that some of the day is
 * already past.
 */
export function caffeineRemainingAt(doses: readonly CaffeineDose[], hour: number): number {
  const total = doses.reduce((sum, dose) => {
    const elapsed = hour - dose.atHour
    if (!Number.isFinite(elapsed) || elapsed < 0) return sum
    return sum + dose.mg * 2 ** (-elapsed / CAFFEINE_HALF_LIFE_HOURS)
  }, 0)
  return Math.round(total)
}
